<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl">
      <icon name="questionnaires" class="w-6 mr-3 fill-gray-700" /> Questionários
    </h1>

    <div class="mb-6 flex justify-between items-center">
      <text-input v-model="form.search" class="w-full max-w-md mr-4" placeholder="Buscar..." @reset="reset" />

      <inertia-link class="btn-indigo" :href="route('questionnaires.create')">
        <span>Criar</span>

        <span class="hidden md:inline">Questionário</span>
      </inertia-link>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Título</th>
          <th class="px-6 pt-6 pb-4">Município/UF</th>
          <th class="px-6 pt-6 pb-4">URL</th>
          <th class="px-6 pt-6 pb-4">Aberto em</th>
        </tr>

        <template v-for="questionnaire in questionnaires">
          <tr :key="questionnaire.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="flex border-t w-full h-full">
              <button
                class="hover:underline ml-5 mr-2 my-auto"
                tabindex="-1"
                type="button"
                @click="destroy(questionnaire.id)"
              >
                <icon name="trash" class="flex-shrink-0 w-4 h-4 fill-red-600" />
              </button>

              <button
                class="hover:underline ml-2 my-auto"
                :class="{
                  'mr-3': questionnaire.answers.length > 0,
                  'mr-21': questionnaire.answers.length === 0
                }"
                tabindex="-1"
                type="button"
                @click="copy(questionnaire.id)"
              >
                <icon name="plus" class="flex-shrink-0 w-4 h-4 fill-green-500" />
              </button>

              <button
                v-if="questionnaire.answers.length > 0"
                class="hover:underline ml-2 my-auto"
                :class="{
                  'mr-3': questionnaire.intelligent,
                  'mr-12': !questionnaire.intelligent
                }"
                tabindex="-1"
                type="button"
                @click="toggle(questionnaire.id, questionnaire.answers)"
              >
                <icon name="chevron-down" class="flex-shrink-0 w-4 h-4 fill-indigo-600" />
              </button>

              <button
                v-if="questionnaire.answers.length > 0 && questionnaire.intelligent"
                class="hover:underline ml-2 mr-3 my-auto"
                tabindex="-1"
                type="button"
                @click="config(questionnaire.id)"
              >
                <icon name="gear" class="flex-shrink-0 w-4 h-4 fill-indigo-d" />
              </button>

              <inertia-link class="ml-2 mr-auto my-auto px-6 py-4 flex items-center" :href="route('questionnaires.edit', questionnaire.id)" tabindex="-1">
                {{ questionnaire.title }}
              </inertia-link>
            </td>

            <td class="border-t">
              <inertia-link class="px-6 py-4 flex items-center" :href="route('questionnaires.edit', questionnaire.id)" tabindex="-1">
                {{ questionnaire.city_name }} - {{ questionnaire.state_code }}
              </inertia-link>
            </td>

            <td class="border-t">
              <a :href="questionnaire.url" target="_blank" class="px-6 py-4 flex items-center" tabindex="-1">
                <span class="text-xs px-2 py-0.5 font-bold bg-gray-100 text-gray-600 rounded">
                  {{ questionnaire.url }}
                </span>
              </a>
            </td>

            <td class="border-t">
              <inertia-link class="px-6 py-4 flex items-center" :href="route('questionnaires.edit', questionnaire.id)" tabindex="-1">
                {{ questionnaire.opened_on }}
              </inertia-link>
            </td>

            <td class="border-t w-px">
              <inertia-link class="px-4 flex items-center" :href="route('questionnaires.edit', questionnaire.id)" tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </inertia-link>
            </td>
          </tr>

          <tr v-if="answers.length > 0 && answersId == questionnaire.id" :key="questionnaire.id * Math.random()" class="hover:bg-gray-100 text-left focus-within:bg-gray-100">
            <td class="border-t px-6 pt-6 pb-4" colspan="4">
              <table class="min-w-full border">
                <thead class="border-b">
                  <tr class="text-left font-bold">
                    <th class="px-6 pt-6 pb-4">Usuário</th>
                    <th class="px-6 pt-6 pb-4">Data</th>
                    <th class="px-6 pt-6 pb-4">URL</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="answer in answers" :key="answer.code" class="hover:bg-gray-100 text-left focus-within:bg-gray-100">
                    <td class="border-t px-6 pt-6 pb-4">
                      {{ answer.user }}
                    </td>

                    <td class="border-t px-6 pt-6 pb-4">
                      {{ answer.date }}
                    </td>

                    <td class="border-t px-6 pt-6 pb-4">
                      <a :href="answer.url" target="_blank" class="px-6 py-4 flex items-center" tabindex="-1">
                        <span class="text-xs px-2 py-0.5 font-bold bg-gray-100 text-gray-600 rounded">
                          {{ answer.url }}
                        </span>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </template>

        <tr v-if="questionnaires.length === 0">
          <td class="border-t px-6 py-4" colspan="4">Nenhum registro</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import TextInput from '../../Shared/TextInput'

export default {
  metaInfo: { title: 'Questionários' },
  components: {
    Icon,
    TextInput,
  },
  layout: Layout,
  props: {
    questionnaires: Array,
    filter: Object,
  },
  data() {
    return {
      form: {
        search: this.filter.search,
      },
      answers: [],
      answersId: null,
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('questionnaires'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    destroy(id) {
      if(confirm('Tem certeza de que deseja excluir este questionário?'))
      {
        this.$inertia.delete(this.route('questionnaires.destroy', id))
      }
    },
    copy(id) {
      if(confirm('Tem certeza de que deseja replicar este questionário?'))
      {
        this.$inertia.post(this.route('questionnaires.copy', id))
      }
    },
    toggle(id, questionnaireAnswers) {
      if(this.answers.length > 0) {
        this.answersId = null
        this.answers = []

        return
      }

      this.answersId = id
      this.answers = questionnaireAnswers
    },
    config(id) {
      this.$inertia.get(this.route('questionnaires.config', id))
    },
  },
}
</script>

<style>
.mr-21 {
  margin-right: 5.5rem;
}
</style>
